import { AxiosError } from "axios";
import { getUiLang } from "@/lib/utils/ui-lang";

/**
 * Returns true if the error is a 401 Unauthorized response.
 * Use this to skip local error display — the Axios interceptor in client.ts
 * already handles 401 by refreshing the token or redirecting to login.
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }
  if (error && typeof error === "object") {
    const err = error as { name?: unknown; status?: unknown };
    return err.name === "AuthRequiredError" || err.status === 401;
  }
  return false;
}

/** Translation key for error messages (errors.unexpected, errors.badRequest, etc.) */
export const ERROR_KEYS = {
  unexpected: "errors.unexpected",
  badRequest: "errors.badRequest",
  unauthorized: "errors.unauthorized",
  forbidden: "errors.forbidden",
  notFound: "errors.notFound",
  conflict: "errors.conflict",
  unprocessable: "errors.unprocessable",
  tooManyRequests: "errors.tooManyRequests",
  serverError: "errors.serverError",
  notImplemented: "errors.notImplemented",
  badGateway: "errors.badGateway",
  serviceUnavailable: "errors.serviceUnavailable",
  gatewayTimeout: "errors.gatewayTimeout",
} as const;

type ErrorTranslator = (key: string) => string;

const FALLBACKS: Record<keyof typeof ERROR_KEYS, { ar: string; en: string }> = {
  unexpected: { ar: "حدث خطأ غير متوقع", en: "An unexpected error occurred" },
  badRequest: { ar: "طلب غير صحيح", en: "Bad request" },
  unauthorized: {
    ar: "غير مصرح لك بالوصول. يرجى تسجيل الدخول",
    en: "You are not authorized to access this. Please sign in",
  },
  forbidden: {
    ar: "غير مصرح لك بهذا الإجراء",
    en: "You are not allowed to perform this action",
  },
  notFound: { ar: "المورد المطلوب غير موجود", en: "The requested resource was not found" },
  conflict: { ar: "تعارض في البيانات", en: "Data conflict" },
  unprocessable: {
    ar: "البيانات المرسلة غير صحيحة",
    en: "The submitted data is invalid",
  },
  tooManyRequests: {
    ar: "تم تجاوز عدد المحاولات المسموح بها. يرجى المحاولة لاحقاً",
    en: "Too many attempts. Please try again later",
  },
  serverError: {
    ar: "خطأ في الخادم. يرجى المحاولة لاحقاً",
    en: "Server error. Please try again later",
  },
  notImplemented: {
    ar: "هذه الميزة غير متاحة حالياً",
    en: "This feature is not available yet",
  },
  badGateway: {
    ar: "خطأ في الاتصال بالخادم",
    en: "Error connecting to the server",
  },
  serviceUnavailable: {
    ar: "الخدمة غير متاحة حالياً. يرجى المحاولة لاحقاً",
    en: "Service is temporarily unavailable. Please try again later",
  },
  gatewayTimeout: {
    ar: "انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى",
    en: "Connection timed out. Please try again",
  },
};

/**
 * Coerces an API error value into a display string.
 * Some backends (e.g. the ERP/NestJS auth) return structured errors like
 * `{ code, message, details }`; returning that object directly would crash the
 * UI with React error #31 ("Objects are not valid as a React child"). We dig
 * out a nested string `message` when present, otherwise return null so callers
 * fall back to a friendly status-based message.
 */
function extractMessageString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (value && typeof value === "object") {
    const nested = (value as { message?: unknown }).message;
    if (typeof nested === "string" && nested.trim().length > 0) {
      return nested.trim();
    }
  }
  return null;
}

function resolveMessage(
  key: keyof typeof ERROR_KEYS,
  t?: ErrorTranslator
): string {
  if (t) {
    try {
      return t(ERROR_KEYS[key]);
    } catch {
      // fall through to fallback
    }
  }
  const lang = getUiLang();
  return FALLBACKS[key][lang];
}

/**
 * Extract a friendly error message from an API error response.
 * Handles various error response formats and status codes.
 * Pass `t` from useTranslations() or getTranslations() to use i18n; otherwise
 * messages fall back to the current UI language (document.documentElement.lang).
 */
export function getErrorMessage(
  error: unknown,
  defaultMessage?: string,
  t?: ErrorTranslator
): string {
  const defaultMsg = defaultMessage ?? resolveMessage("unexpected", t);

  // If it's already a string, return it
  if (typeof error === "string") {
    return error;
  }

  // Handle Axios errors
  if (error instanceof AxiosError) {
    const response = error.response;

    // Try to extract message from response data
    if (response?.data) {
      const message =
        extractMessageString(response.data.message) ??
        extractMessageString(response.data.error) ??
        extractMessageString(response.data.Message) ??
        extractMessageString(response.data.Error) ??
        extractMessageString(response.data.errorMessage) ??
        extractMessageString(response.data.ErrorMessage);

      if (message) {
        return message;
      }

      const nestedMessage = extractMessageString(response.data.data?.message);
      if (nestedMessage) {
        return nestedMessage;
      }

      if (response.data.errors && Array.isArray(response.data.errors)) {
        return response.data.errors.join(", ");
      }

      if (response.data.errors && typeof response.data.errors === "object") {
        const errorMessages = Object.values(response.data.errors).flat();
        if (errorMessages.length > 0) {
          return errorMessages.join(", ");
        }
      }
    }

    // Handle specific status codes with friendly messages
    const status = response?.status;
    switch (status) {
      case 400:
        return (
          extractMessageString(response?.data?.message) ??
          resolveMessage("badRequest", t)
        );
      case 401:
        return resolveMessage("unauthorized", t);
      case 403:
        return resolveMessage("forbidden", t);
      case 404:
        return resolveMessage("notFound", t);
      case 409:
        return (
          extractMessageString(response?.data?.message) ??
          resolveMessage("conflict", t)
        );
      case 422:
        return (
          extractMessageString(response?.data?.message) ??
          resolveMessage("unprocessable", t)
        );
      case 429:
        return resolveMessage("tooManyRequests", t);
      case 500:
        return resolveMessage("serverError", t);
      case 501:
        return (
          extractMessageString(response?.data?.message) ??
          resolveMessage("notImplemented", t)
        );
      case 502:
        return resolveMessage("badGateway", t);
      case 503:
        return resolveMessage("serviceUnavailable", t);
      case 504:
        return resolveMessage("gatewayTimeout", t);
      default:
        if (error.message) {
          return error.message;
        }
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    if (isAuthError(error)) {
      return resolveMessage("unauthorized", t);
    }
    return error.message;
  }

  return defaultMsg;
}
